'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import LcIcon from './icons'

type Props = {
  value: string
  label?: string
  className?: string
  size?: 20 | 24
  disabled?: boolean
}

/** Figma 20px copy chip (radius 6, hairline ring). Swaps to a check for 1.2s after copying. */
const CopyButton = ({ value, label = 'Copy', className, size = 20, disabled }: Props) => {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(timer.current), [])

  const handleCopy = useCallback(async () => {
    if (disabled) return
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 1200)
    } catch {
      /* clipboard unavailable — nothing to recover */
    }
  }, [value, disabled])

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
      disabled={disabled}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-[6px] bg-surface text-ink-3 outline-none',
        'shadow-[0_0_0_1px_#F2F2F2,0_1.5px_1.5px_rgba(1,54,151,0.04),0_0.5px_0.5px_rgba(1,54,151,0.04)] dark:shadow-[0_0_0_1px_rgb(var(--lc-line))]',
        'transition-[transform,color,background-color] duration-[160ms] ease-out-strong hover:text-ink active:scale-[0.92] focus-visible:ring-2 focus-visible:ring-brand/40',
        'disabled:cursor-not-allowed disabled:opacity-50',
        copied && 'text-success hover:text-success',
        className
      )}
      style={{ width: size, height: size }}
    >
      <span key={String(copied)} className="inline-flex animate-lc-pop">
        {copied ? <Check size={size === 20 ? 12 : 14} strokeWidth={2} /> : <LcIcon name="copy" size={size === 20 ? 12 : 14} />}
      </span>
    </button>
  )
}

export default CopyButton
